import {
  EActionUsageKind,
  IActionUsageEntityDto,
  IActionUsageLocationDto,
  IActionUsageReportDto,
  IActionUsageScreenDto
} from "@kuika/kuika-cl-model";
import { AxiosResponse } from "axios";
import * as React from "react";
import { DatasourceService } from "../../../../services/datasource-service";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { ActionUsageTarget, navigateToActionUsage } from "../../../../shared/utility/action-usage-navigation";
import { KModal } from "../../../ui-kit/k-modal/k-modal";
import KuikaBadge, { KuikaBadgeColor } from "../../../ui-kit/kuika-badge/kuika-badge";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaSkeleton from "../../../ui-kit/kuika-skeleton/kuika-skeleton";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import KuikaText from "../../../ui-kit/typography/Text/Text";
import KuikaTextBold from "../../../ui-kit/typography/TextBold/TextBold";
import KuikaTextMedium from "../../../ui-kit/typography/TextMedium/TextMedium";
import "./action-usages-modal.scss";

/**
 * Loading is not one state (KOA-17818):
 *  - `loading`   — opened cold, nothing to show yet; a skeleton shaped like the answer.
 *  - `reloading` — a different action was inspected while the report was open; the previous answer
 *                  stays on screen, dimmed, under a progress bar. Replacing a correct answer with a
 *                  skeleton would throw away information to show nothing.
 */
type ActionUsagesPhase = "loading" | "reloading" | "ready" | "error";

/** Past this, the wait is long enough that silence reads as breakage — say what is being searched. */
const SLOW_SCAN_NOTICE_MS: number = 2000;

export interface IActionUsagesModalProps {
  showModal: boolean;
  closeModal: () => void;
  actionId: string | undefined;
  /** Known before the request starts, so the name renders in the first frame rather than shimmering. */
  actionName?: string;
}

export interface IActionUsagesModalState {
  phase: ActionUsagesPhase;
  isSlow: boolean;
  report: IActionUsageReportDto | undefined;
}

interface IUsageKindPresentation {
  label: string;
  color: KuikaBadgeColor;
}

/** One non-screen group (processes, scheduled jobs, API Builder) as it is rendered. */
interface IEntityGroup {
  title: string;
  icon: string;
  entities: IActionUsageEntityDto[];
  targetKind: ActionUsageTarget["kind"];
}

const USAGE_KIND_PRESENTATION: Record<EActionUsageKind, IUsageKindPresentation> = {
  [EActionUsageKind.Event]: { label: "Event", color: "blue" },
  [EActionUsageKind.DataBinding]: { label: "Data Binding", color: "green" },
  [EActionUsageKind.Parameter]: { label: "Parameter", color: "purple" },
  [EActionUsageKind.Condition]: { label: "Condition", color: "orange" },
  [EActionUsageKind.Unknown]: { label: "Reference", color: "grey" }
};

function pluralize(count: number, singular: string, plural: string): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

/**
 * The one-line answer to "how bad is this change?", rendered before any list. Exported for tests —
 * the joining and pluralisation are the whole point and are easy to get subtly wrong.
 */
export function buildSummarySentence(report: IActionUsageReportDto): string {
  const parts: string[] = [];
  if (report.screens.length > 0) {
    parts.push(pluralize(report.screens.length, "screen", "screens"));
  }
  if (report.workflows.length > 0) {
    parts.push(pluralize(report.workflows.length, "process", "processes"));
  }
  if (report.scheduledJobs.length > 0) {
    parts.push(pluralize(report.scheduledJobs.length, "scheduled job", "scheduled jobs"));
  }
  if (report.apiEndpoints.length > 0) {
    parts.push(pluralize(report.apiEndpoints.length, "API endpoint", "API endpoints"));
  }

  if (parts.length === 0) {
    return "Not used anywhere.";
  }
  if (parts.length === 1) {
    return `Used in ${parts[0]}.`;
  }
  const last: string = parts[parts.length - 1];
  const rest: string = parts.slice(0, -1).join(", ");
  return `Used in ${rest} and ${last}.`;
}

/**
 * KOA-17818 — "Where Used" for an action: every screen that uses it, the element and event or
 * property inside that screen, plus the processes, scheduled jobs and API Builder endpoints that
 * consume it, each with a way to open it.
 *
 * The action name lives in the body, not the modal header: `KModal` uppercases its header, which
 * turns a long action name into a shouting, truncating title.
 */
export class ActionUsagesModalComponent extends React.PureComponent<
  IActionUsagesModalProps & IWithSDProps,
  IActionUsagesModalState
> {
  private isMounted_: boolean = false;

  private slowScanTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(props: IActionUsagesModalProps & IWithSDProps) {
    super(props);
    this.state = { phase: "loading", isSlow: false, report: undefined };
  }

  componentDidMount(): void {
    this.isMounted_ = true;
    if (this.props.showModal) {
      void this.loadUsages();
    }
  }

  componentDidUpdate(prevProps: IActionUsagesModalProps & IWithSDProps): void {
    const hasJustOpened: boolean = this.props.showModal && !prevProps.showModal;
    const hasActionChanged: boolean = this.props.actionId !== prevProps.actionId;
    if (this.props.showModal && (hasJustOpened || hasActionChanged)) {
      void this.loadUsages();
    }
  }

  componentWillUnmount(): void {
    // The fetch is async; guard setState if the modal closes mid-flight.
    this.isMounted_ = false;
    this.clearSlowScanTimer();
  }

  private clearSlowScanTimer(): void {
    if (this.slowScanTimer !== null) {
      clearTimeout(this.slowScanTimer);
      this.slowScanTimer = null;
    }
  }

  loadUsages = async (): Promise<void> => {
    const actionId: string | undefined = this.props.actionId;
    if (!actionId) {
      return;
    }

    // A report already on screen is kept and dimmed rather than torn down.
    const hasPreviousAnswer: boolean = this.state.report !== undefined;
    this.setState({ phase: hasPreviousAnswer ? "reloading" : "loading", isSlow: false });

    this.clearSlowScanTimer();
    this.slowScanTimer = setTimeout((): void => {
      if (this.isMounted_) {
        this.setState({ isSlow: true });
      }
    }, SLOW_SCAN_NOTICE_MS);

    const response: AxiosResponse<IActionUsageReportDto> = await DatasourceService.GetActionUsages(actionId);
    this.clearSlowScanTimer();
    if (!this.isMounted_) {
      return;
    }

    const isOk: boolean = response?.status === 200 && !!response.data;
    if (!isOk) {
      // Never fall back to the empty state here: "not used anywhere" invites an edit, and that is
      // the one wrong answer that actively causes damage.
      this.setState({ phase: "error", isSlow: false, report: undefined });
      return;
    }

    this.setState({ phase: "ready", isSlow: false, report: response.data });
  };

  open = (target: ActionUsageTarget): void => {
    this.props.closeModal();
    void navigateToActionUsage(this.props.sd, target);
  };

  renderOpenButton = (target: ActionUsageTarget, label: string): React.ReactNode => (
    <KuikaButton type="text" size="sm" onClick={() => this.open(target)} aria-label={label}>
      Open
    </KuikaButton>
  );

  renderGroupHeading = (title: string, count: number): React.ReactNode => (
    <div className="kuika-action-usages__group-heading">
      <span className="kuika-action-usages__group-heading-label">{title}</span>
      <span className="kuika-action-usages__group-heading-rule" />
      <KuikaSmallText>{`${count}`}</KuikaSmallText>
    </div>
  );

  renderLocation = (location: IActionUsageLocationDto, index: number): React.ReactNode => {
    const presentation: IUsageKindPresentation =
      USAGE_KIND_PRESENTATION[location.kind] ?? USAGE_KIND_PRESENTATION[EActionUsageKind.Unknown];

    return (
      <div className="kuika-action-usages__location" key={`${location.elementId}-${location.kind}-${index}`}>
        <KuikaTextMedium>{location.elementName}</KuikaTextMedium>
        {location.detail && <code className="kuika-action-usages__detail">{location.detail}</code>}
        <span className="kuika-action-usages__spacer" />
        <KuikaBadge content={presentation.label} color={presentation.color} size="small" />
      </div>
    );
  };

  renderScreen = (screen: IActionUsageScreenDto): React.ReactNode => (
    <div className="kuika-action-usages__card" key={screen.screenId}>
      <div className="kuika-action-usages__card-header">
        <KuikaSvg name="screen" width={16} height={16} />
        <KuikaTextMedium>{screen.screenName}</KuikaTextMedium>
        <span className="kuika-action-usages__spacer" />
        {this.renderOpenButton({ kind: "screen", id: screen.screenId }, `Open ${screen.screenName}`)}
      </div>

      {screen.isPartial ? (
        <div className="kuika-action-usages__note">
          <KuikaSvg name="info-circle" width={14} height={14} />
          <KuikaSmallText>Used on this screen. The exact element could not be resolved.</KuikaSmallText>
        </div>
      ) : (
        screen.locations.map(this.renderLocation)
      )}
    </div>
  );

  renderEntityGroup = (group: IEntityGroup): React.ReactNode => {
    if (group.entities.length === 0) {
      return null;
    }

    return (
      <div className="kuika-action-usages__group" key={group.title}>
        {this.renderGroupHeading(group.title, group.entities.length)}
        {group.entities.map((entity: IActionUsageEntityDto) => (
          <div className="kuika-action-usages__card" key={entity.id}>
            <div className="kuika-action-usages__card-header">
              <KuikaSvg name={group.icon} type="topbar-icons" width={16} height={16} />
              <KuikaTextMedium>{entity.name}</KuikaTextMedium>
              <span className="kuika-action-usages__spacer" />
              <KuikaSmallText>{entity.projectName}</KuikaSmallText>
              {this.renderOpenButton({ kind: group.targetKind, id: entity.id }, `Open ${entity.name}`)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  renderSlowNotice = (): React.ReactNode => (
    <div className="kuika-action-usages__slow">
      <span className="kuika-action-usages__spinner" />
      <KuikaText>Searching screens, processes, jobs and API Builder…</KuikaText>
    </div>
  );

  renderSkeleton = (): React.ReactNode => (
    <div className="kuika-action-usages" aria-busy="true" aria-live="polite">
      {this.renderSummary(this.state.isSlow ? this.renderSlowNotice() : <KuikaSkeleton width={220} height={14} />)}

      <div className="kuika-action-usages__group">
        <div className="kuika-action-usages__group-heading">
          <KuikaSkeleton width={56} height={11} />
          <span className="kuika-action-usages__group-heading-rule" />
        </div>
        <div className="kuika-action-usages__card">
          <div className="kuika-action-usages__card-header">
            <KuikaSkeleton width={16} height={16} />
            <KuikaSkeleton width={132} height={14} />
          </div>
          {[104, 138, 118].map((nameWidth: number) => (
            <div className="kuika-action-usages__location" key={nameWidth}>
              <KuikaSkeleton width={nameWidth} height={14} />
              <KuikaSkeleton width={62} height={12} />
              <span className="kuika-action-usages__spacer" />
              <KuikaSkeleton width={54} height={18} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  renderSummary = (secondLine: React.ReactNode): React.ReactNode => (
    <div className="kuika-action-usages__summary">
      <KuikaTextBold>{this.props.actionName ?? ""}</KuikaTextBold>
      {secondLine}
    </div>
  );

  renderGroups = (report: IActionUsageReportDto): React.ReactNode => {
    const entityGroups: IEntityGroup[] = [
      {
        title: "Processes",
        icon: "process-automation",
        entities: report.workflows,
        targetKind: "workflow"
      },
      {
        title: "Scheduled Jobs",
        icon: "scheduled-jobs",
        entities: report.scheduledJobs,
        targetKind: "scheduledJob"
      },
      {
        title: "API Builder",
        icon: "api-management",
        entities: report.apiEndpoints,
        targetKind: "apiEndpoint"
      }
    ];

    if (report.totalCount === 0) {
      return (
        <div className="kuika-action-usages__empty">
          <KuikaSvg name="search-off" width={32} height={32} />
          <KuikaTextMedium>No screen, process, scheduled job or API endpoint uses this action.</KuikaTextMedium>
          <KuikaText>You can change it without affecting anything in this app.</KuikaText>
        </div>
      );
    }

    return (
      <>
        {report.screens.length > 0 && (
          <div className="kuika-action-usages__group">
            {this.renderGroupHeading("Screens", report.screens.length)}
            {report.screens.map(this.renderScreen)}
          </div>
        )}
        {entityGroups.map(this.renderEntityGroup)}
      </>
    );
  };

  renderScope = (): React.ReactNode => (
    <div className="kuika-action-usages__scope">
      Screens are searched in this app only. Processes, scheduled jobs and API Builder are searched across the
      workspace.
    </div>
  );

  renderError = (): React.ReactNode => (
    <div className="kuika-action-usages">
      <div className="kuika-action-usages__error">
        <KuikaSvg name="alert-triangle" width={20} height={20} />
        <div className="kuika-action-usages__error-text">
          <KuikaTextMedium>Usage information could not be loaded.</KuikaTextMedium>
          <KuikaText>Close this window and try again. Do not assume the action is unused.</KuikaText>
        </div>
      </div>
    </div>
  );

  renderBody = (): React.ReactNode => {
    if (this.state.phase === "error") {
      return this.renderError();
    }
    if (this.state.phase === "loading") {
      return this.renderSkeleton();
    }

    const report: IActionUsageReportDto | undefined = this.state.report;
    if (!report) {
      return null;
    }

    if (this.state.phase === "reloading") {
      // The name is the NEW action's and renders crisp; the groups below still describe the
      // previous one, so they are dimmed and the sentence between them is withheld rather than
      // shown stale under a name it no longer belongs to.
      return (
        <>
          <div className="kuika-action-usages__progress" />
          <div className="kuika-action-usages">
            {this.renderSummary(<KuikaText>Updating…</KuikaText>)}
            <div className="kuika-action-usages__stale" aria-busy="true">
              {this.renderGroups(report)}
            </div>
          </div>
        </>
      );
    }

    return (
      <div className="kuika-action-usages">
        {this.renderSummary(<KuikaText>{buildSummarySentence(report)}</KuikaText>)}
        {this.renderGroups(report)}
        {this.renderScope()}
      </div>
    );
  };

  render(): React.ReactNode {
    return (
      <KModal
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        header="Where Used"
        width={600}
        centered
        dontRenderSpinner
      >
        {this.renderBody()}
      </KModal>
    );
  }
}

export const ActionUsagesModal = withSD<IActionUsagesModalProps>(ActionUsagesModalComponent);
